import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, Check } from 'lucide-react';
import ScheduleMeetingModal from '../components/ScheduleMeetingModal';
import { supabase } from '../utils/supabase';

interface Double {
  id: string;
  name: string;
  scheduling_link: string;
  meeting_duration: number;
  status?: string;
}

const Schedule = () => {
  const { name } = useParams<{ name: string }>();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [double, setDouble] = useState<Double | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);

  useEffect(() => {
    const fetchDouble = async () => {
      try {
        // Update page title when double is loaded
        document.title = name ? `Schedule with ${name}` : 'Schedule Meeting';

        // Ensure name is properly normalized to handle mobile/desktop differences
        if (!name) {
          setError('Invalid scheduling link');
          setLoading(false);
          return;
        }

        // Normalize scheduling link by trimming whitespace and removing trailing slashes
        const normalizedName = name.toLowerCase().trim().replace(/\/$/, '');
        
        // For debugging - you can remove these logs in production
        console.log('Original name param:', name);
        console.log('Normalized name:', normalizedName);
        console.log('User agent:', navigator.userAgent);

        // Use ilike for case-insensitive matching instead of eq
        const { data, error } = await supabase
          .from('doubles')
          .select('id, name, scheduling_link, meeting_duration, status')
          .ilike('scheduling_link', normalizedName)
          .single();

        if (error) throw error;
        
        // Check if double is active
        if (data && data.status !== 'active') {
          setError('This double is not currently accepting meetings');
          document.title = 'Schedule Meeting';
          setLoading(false);
          return;
        }
        
        setDouble(data);
        document.title = `Schedule with ${data.name}`;
      } catch (err) {
        console.error('Error fetching double:', err);
        setError('Double not found');
        document.title = 'Schedule Meeting';
      } finally {
        setLoading(false);
      }
    };

    if (name) {
      fetchDouble();
    } else {
      document.title = 'Schedule Meeting';
    }

    // Reset title when component unmounts
    return () => {
      document.title = 'Double Me | Be Present Everywhere';
    };
  }, [name]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
        isPast: new Date(year, month - 1, prevMonthLastDay - i) < new Date().setHours(0, 0, 0, 0)
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      days.push({
        date: currentDate,
        isCurrentMonth: true,
        isPast: currentDate < new Date().setHours(0, 0, 0, 0)
      });
    }
    
    // Add days from next month
    const remainingDays = 42 - days.length; // Always show 6 weeks
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isPast: false
      });
    }
    
    return days;
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isToday = (date: Date) => {
    return formatDate(date) === formatDate(today);
  };

  const isPastDate = (date: Date) => {
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const handleScheduleSubmit = async (data: any) => {
    try {
      // Save the booking data
      const { error } = await supabase
        .from('meeting_requests')
        .insert([
          {
            double_id: double?.id,
            date: data.selectedDate,
            times: data.selectedTimes,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phone,
            topic: data.meetingTopic,
            duration: data.durationInMinutes,
            status: 'pending'
          }
        ]);

      if (error) throw error;
      
      // Set booking data for confirmation
      setBookingData(data);
      
      // Show confirmation
      setShowScheduleModal(false);
      setShowConfirmation(true);
      
    } catch (err) {
      console.error('Error booking meeting:', err);
      // You may want to show an error message to the user
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !double) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md bg-white p-8 rounded-xl border-2 border-dark shadow-retro text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Schedule not found</h1>
          <p className="text-gray-600 mb-6">The scheduling link you're trying to access doesn't exist or is currently unavailable.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-3 rounded-full transition-all border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md bg-white p-8 rounded-xl border-2 border-dark shadow-retro text-center">
          <div className="w-16 h-16 bg-green-100 mx-auto rounded-full flex items-center justify-center mb-6">
            <Check className="text-green-600 w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Thank you!</h1>
          <p className="text-gray-700 mb-2">
            Your meeting request with {double.name} has been submitted.
          </p>
          <p className="text-gray-600 mb-6">
            We'll notify you when one of your suggested times is confirmed.
          </p>
          
          {bookingData && (
            <div className="bg-gray-50 p-4 rounded-lg text-left mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Your suggested times:</h3>
              <p className="text-gray-600 mb-1">{new Date(bookingData.selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
              <ul className="list-disc list-inside">
                {bookingData.selectedTimes.map((time: string, index: number) => (
                  <li key={index} className="text-gray-600">{time}</li>
                ))}
              </ul>
            </div>
          )}
          
          <button
            onClick={() => window.location.href = '/'}
            className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-3 rounded-full transition-all border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Schedule with {double.name}
          </h1>
          <p className="text-gray-600">
            Select a date and time that works best for you
          </p>
          <div className="flex items-center justify-center mt-4">
            <Clock className="w-5 h-5 text-primary mr-2" />
            <span className="text-gray-600">
              {Math.ceil((double.meeting_duration || 1800) / 60)} minute meeting
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border-2 border-dark shadow-retro">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h2 className="text-xl font-semibold min-w-40 text-center">{monthYear}</h2>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => (
              <div
                key={index}
                className={`aspect-square p-2 rounded-lg border-2 relative flex items-center justify-center
                  transition-all cursor-pointer hover:shadow-none hover:-translate-y-1
                  ${isToday(day.date) ? 'bg-primary/10 border-primary font-bold' : ''}
                  ${day.isCurrentMonth ? 'bg-white shadow-retro border-dark' : 'opacity-40 bg-gray-50 border-gray-200'}
                  ${day.isPast ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                onClick={() => {
                  if (!day.isPast) {
                    setSelectedDate(formatDate(day.date));
                    setShowScheduleModal(true);
                  }
                }}
              >
                <div>{day.date.getDate()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {showScheduleModal && selectedDate && (
        <ScheduleMeetingModal
          selectedDate={selectedDate}
          meetingDuration={double.meeting_duration}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedDate(null);
          }}
          onSubmit={handleScheduleSubmit}
        />
      )}
    </div>
  );
};

export default Schedule;
